import { useEffect, useState } from "react";
import i18n from "locales/i18n";
import {
  BookingFragment,
  BookingInput,
  useCreateBookingMutation,
  useDeleteBookingMutation,
  useMyInvitedBookingsQuery,
  useRespondToBookingMutation,
  useUpdateBookingMutation,
  useVendorBookingsQuery,
} from "generated/graphql";
import { useS3 } from "hooks/useS3";
import { BookingType } from "../eventTypes";
import { AttendeeStatus, Booking } from "./types";

const ATTACHMENT_FOLDER = "booking-attachments";

const toBooking = (b: BookingFragment): Booking => ({
  id: String(b.id),
  vendorId: b.vendorId ?? undefined,
  title: b.title,
  type: b.type as BookingType,
  date: b.date,
  from: b.fromTime,
  to: b.toTime,
  allDay: b.allDay,
  location: b.location ?? undefined,
  description: b.description ?? undefined,
  attendees: b.attendees.map((a) => ({
    email: a.email,
    name: a.name ?? undefined,
    optional: a.optional ?? undefined,
    status: (a.status as AttendeeStatus) ?? undefined,
    proposedDate: a.proposedDate ?? undefined,
    proposedFrom: a.proposedFrom ?? undefined,
    proposedTo: a.proposedTo ?? undefined,
  })),
  attachments: b.attachments.map((at) => ({
    name: at.name,
    url: at.url ?? undefined,
  })),
  status: b.status ?? undefined,
  meetingLink: b.meetingLink ?? undefined,
  vendorNote: b.vendorNote ?? undefined,
  quoteId: b.quote?.id,
  quote: b.quote
    ? {
        id: b.quote.id,
        estimateNumber: b.quote.estimateNumber ?? undefined,
        totalCost: b.quote.totalCost ?? undefined,
        chargerModel: b.quote.chargerModel ?? undefined,
        stage: b.quote.stage ?? undefined,
      }
    : undefined,
  createdByName: b.createdByName ?? undefined,
  createdByRole: (b.createdByRole as "vendor" | "customer" | null) ?? undefined,
  createdAt: b.createdAt ?? undefined,
});

const toInput = (b: Booking): BookingInput => ({
  title: b.title,
  type: b.type,
  date: b.date,
  fromTime: b.from,
  toTime: b.to,
  allDay: b.allDay ?? false,
  location: b.location,
  description: b.description,
  meetingLink: b.meetingLink,
  vendorNote: b.vendorNote,
  quoteId: b.quoteId,
  attendees: b.attendees.map((a) => ({
    email: a.email,
    name: a.name,
    optional: a.optional,
    status: a.status,
    proposedDate: a.proposedDate,
    proposedFrom: a.proposedFrom,
    proposedTo: a.proposedTo,
  })),
  attachments: b.attachments.map((at) => ({ name: at.name, url: at.url })),
});

const tc = i18n.getFixedT(null, "calendar");
export interface SaveBookingResult {
  ok: boolean;
  error?: string;
  booking?: Booking;
}

export const useVendorBookings = (isVendor: boolean) => {
  const [{ data, fetching }] = useVendorBookingsQuery({
    pause: !isVendor,
    requestPolicy: "cache-and-network",
  });
  const [, createBooking] = useCreateBookingMutation();
  const [, updateBooking] = useUpdateBookingMutation();
  const [, deleteBookingMut] = useDeleteBookingMutation();
  const { uploadToS3 } = useS3();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isVendor) {
      setBookings([]);
      setLoaded(true);
      return;
    }
    if (!fetching && data?.vendorBookings) {
      setBookings(data.vendorBookings.map(toBooking));
      setLoaded(true);
    }
  }, [isVendor, fetching, data]);

  // Upload any newly-attached files to S3, returning the full attachment list.
  const withUploadedAttachments = async (
    booking: Booking,
    newFiles: File[],
  ) => {
    const uploaded = await Promise.all(
      newFiles.map(async (file) => ({
        name: file.name,
        url: (await uploadToS3(file, ATTACHMENT_FOLDER)) ?? undefined,
      })),
    );
    return [...booking.attachments, ...uploaded];
  };

  const save = async (
    booking: Booking,
    newFiles: File[],
  ): Promise<SaveBookingResult> => {
    const attachments = await withUploadedAttachments(booking, newFiles);
    const input = toInput({ ...booking, attachments });
    const isEdit = bookings.some((b) => b.id === booking.id);

    let errors: { message?: string | null }[] | undefined | null;
    let savedFragment: BookingFragment | undefined | null;

    if (isEdit) {
      const res = await updateBooking({ id: Number(booking.id), input });
      if (res.error) {
        return { ok: false, error: res.error.message };
      }
      errors = res.data?.updateBooking.errors;
      savedFragment = res.data?.updateBooking.booking;
    } else {
      const res = await createBooking({ input });
      if (res.error) {
        return { ok: false, error: res.error.message };
      }
      errors = res.data?.createBooking.errors;
      savedFragment = res.data?.createBooking.booking;
    }

    if (errors && errors.length > 0) {
      return {
        ok: false,
        error: errors[0].message ?? "Failed to save booking",
      };
    }

    const saved = savedFragment ? toBooking(savedFragment) : undefined;
    if (saved) {
      setBookings((prev) =>
        prev.some((b) => b.id === saved.id)
          ? prev.map((b) => (b.id === saved.id ? saved : b))
          : [...prev, saved],
      );
    }
    return { ok: true, booking: saved };
  };

  const remove = async (id: string): Promise<SaveBookingResult> => {
    const res = await deleteBookingMut({ id: Number(id) });
    if (res.error || !res.data?.deleteBooking) {
      return {
        ok: false,
        error: res.error?.message ?? "Failed to delete booking",
      };
    }
    setBookings((prev) => prev.filter((b) => b.id !== id));
    return { ok: true };
  };

  return { bookings, loading: !loaded, save, remove };
};

export type BookingResponseType = "accept" | "decline" | "reschedule";

export interface ProposedTime {
  date: string;
  from: string;
  to: string;
}

// Customer side: bookings the logged-in account is invited to, plus the
// accept / decline / propose-reschedule action.
export const useInvitedBookings = (enabled: boolean) => {
  const [{ data, fetching }] = useMyInvitedBookingsQuery({
    pause: !enabled,
    requestPolicy: "cache-and-network",
  });
  const [, respondMut] = useRespondToBookingMutation();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setBookings([]);
      setLoaded(true);
      return;
    }
    if (!fetching && data?.myInvitedBookings) {
      setBookings(data.myInvitedBookings.map(toBooking));
      setLoaded(true);
    }
  }, [enabled, fetching, data]);

  const respond = async (
    bookingId: string,
    response: BookingResponseType,
    proposed?: ProposedTime,
  ): Promise<SaveBookingResult> => {
    const res = await respondMut({
      bookingId: Number(bookingId),
      response,
      proposedDate: proposed?.date,
      proposedFrom: proposed?.from,
      proposedTo: proposed?.to,
    });
    const payload = res.data?.respondToBooking;
    if (res.error || (payload?.errors && payload.errors.length > 0)) {
      return {
        ok: false,
        error:
          payload?.errors?.[0]?.message ??
          res.error?.message ??
          tc("snack.respond_failed"),
      };
    }
    const saved = payload?.booking ? toBooking(payload.booking) : undefined;
    if (saved) {
      setBookings((prev) => prev.map((b) => (b.id === saved.id ? saved : b)));
    }
    return { ok: true, booking: saved };
  };

  return { bookings, loading: !loaded, respond };
};
